import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLanguage, SUPPORTED_LANGUAGES, SupportedLanguage } from '../../../i18n';
import { clearMediaCache } from '../../../utils/cacheUtils';
import regionNames from '../../../i18n/regionNames.json';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** ISO 3166-1 alpha-2 → flag emoji (Regional Indicator Symbols) */
const flagEmoji = (iso: string): string =>
  [...iso.toUpperCase()].map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65)).join('');

// ─── Constants ───────────────────────────────────────────────────────────────

const LANGUAGE_OPTIONS: { code: SupportedLanguage; nativeName: string }[] = [
  { code: 'pt-BR', nativeName: `${flagEmoji('BR')} Português (Brasil)` },
  { code: 'en',    nativeName: `${flagEmoji('US')} English` },
  { code: 'es',    nativeName: `${flagEmoji('ES')} Español` },
  { code: 'fr',    nativeName: `${flagEmoji('FR')} Français` },
  { code: 'de',    nativeName: `${flagEmoji('DE')} Deutsch` },
  { code: 'ar',    nativeName: `${flagEmoji('SA')} العربية` },
  { code: 'it',    nativeName: `${flagEmoji('IT')} Italiano` },
  { code: 'tr',    nativeName: `${flagEmoji('TR')} Türkçe` },
];

const TMDB_LANG_OPTIONS: { value: string; label: string }[] = [
  { value: 'pt-BR', label: `${flagEmoji('BR')} Português (Brasil)` },
  { value: 'en-US', label: `${flagEmoji('US')} English (US)` },
  { value: 'es-ES', label: `${flagEmoji('ES')} Español (España)` },
  { value: 'fr-FR', label: `${flagEmoji('FR')} Français` },
  { value: 'de-DE', label: `${flagEmoji('DE')} Deutsch` },
  { value: 'ar',    label: `${flagEmoji('SA')} العربية` },
  { value: 'it-IT', label: `${flagEmoji('IT')} Italiano` },
  { value: 'tr-TR', label: `${flagEmoji('TR')} Türkçe` },
];

/** Maps app language code → auto TMDB language */
const LANG_TO_TMDB_LANG: Record<string, string> = {
  'pt-BR': 'pt-BR',
  'en': 'en-US',
  'es': 'es-ES',
  'fr': 'fr-FR',
  'de': 'de-DE',
  'ar': 'ar',
  'it': 'it-IT',
  'tr': 'tr-TR',
};

/** Maps app language code → auto TMDB region */
const LANG_TO_REGION: Record<string, string> = {
  'pt-BR': 'BR',
  'en': 'US',
  'es': 'ES',
  'fr': 'FR',
  'de': 'DE',
  'ar': 'SA',
  'it': 'IT',
  'tr': 'TR',
};

// ─── Custom Select Component ─────────────────────────────────────────────────

interface SelectOption {
  value: string;
  label: string;
}

interface SettingsSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  initialFocus?: boolean;
}

const SettingsSelect: React.FC<SettingsSelectProps> = ({ options, value, onChange, initialFocus }) => {
  const [open, setOpen] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialFocus) triggerRef.current?.focus();
  }, [initialFocus]);

  // Scroll focused item into view when list opens or focused item changes
  useEffect(() => {
    if (open && listRef.current) {
      const items = listRef.current.querySelectorAll<HTMLDivElement>('[data-item]');
      items[focusedIdx]?.scrollIntoView({ block: 'nearest' });
    }
  }, [open, focusedIdx]);

  const openList = useCallback(() => {
    const idx = Math.max(0, options.findIndex((o) => o.value === value));
    setFocusedIdx(idx);
    setOpen(true);
    // Focus the list on next tick after render
    setTimeout(() => listRef.current?.focus(), 0);
  }, [options, value]);

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      openList();
    }
    // ArrowDown/ArrowUp intentionally not handled here:
    // they bubble up to Settings which moves focus between dropdown triggers.
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    // Prevent Settings outer handler from seeing any key while dropdown is open
    e.stopPropagation();
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIdx((i) => Math.min(options.length - 1, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIdx((i) => Math.max(0, i - 1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onChange(options[focusedIdx].value);
        setOpen(false);
        triggerRef.current?.focus();
        break;
      case 'Escape':
      case 'Backspace':
      case 'GoBack':
      case 'BrowserBack':
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
    }
  };

  const selectedOption = options.find((o) => o.value === value);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={triggerRef}
        type="button"
        className="settings-select-btn"
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className="settings-select-btn__label">{selectedOption?.label ?? '—'}</span>
        <span className="settings-select-btn__arrow">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <>
          <div
            className="settings-select-backdrop"
            onClick={() => { setOpen(false); triggerRef.current?.focus(); }}
          />
          <div
            ref={listRef}
            className="settings-select-list"
            tabIndex={-1}
            onKeyDown={handleListKeyDown}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
            }}
          >
          {options.map((opt, idx) => (
            <div
              key={opt.value}
              data-item
              className={[
                'settings-select-item',
                idx === focusedIdx ? 'is-focused' : '',
                opt.value === value ? 'is-selected' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onMouseEnter={() => setFocusedIdx(idx)}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
                triggerRef.current?.focus();
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
};

// ─── Section ─────────────────────────────────────────────────────────────────

interface LanguageSectionProps {
  focused: boolean;
}

const LanguageSection: React.FC<LanguageSectionProps> = ({ focused }) => {
  const { t, i18n } = useTranslation();

  const currentLang = (localStorage.getItem('ott_lang') || i18n.language || 'pt-BR') as SupportedLanguage;
  const [currentTmdbLang, setCurrentTmdbLang] = useState(localStorage.getItem('ott_tmdb_lang') || '');
  const [currentTmdbRegion, setCurrentTmdbRegion] = useState(localStorage.getItem('ott_tmdb_region') || '');

  const handleAppLangChange = async (lang: string) => {
    if (SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)) {
      await clearMediaCache();
      sessionStorage.setItem('ott_restore_settings_section', 'language');
      changeLanguage(lang);
    }
  };

  const handleTmdbLangChange = async (val: string) => {
    setCurrentTmdbLang(val);
    if (val) localStorage.setItem('ott_tmdb_lang', val);
    else localStorage.removeItem('ott_tmdb_lang');
    await clearMediaCache();
    sessionStorage.setItem('ott_restore_settings_section', 'language');
    window.location.reload();
  };

  const handleTmdbRegionChange = async (val: string) => {
    setCurrentTmdbRegion(val);
    if (val) localStorage.setItem('ott_tmdb_region', val);
    else localStorage.removeItem('ott_tmdb_region');
    await clearMediaCache();
    sessionStorage.setItem('ott_restore_settings_section', 'language');
    window.location.reload();
  };

  // Auto-resolved values
  const autoTmdbLangValue = LANG_TO_TMDB_LANG[currentLang] || 'en-US';
  const autoTmdbLangLabel = TMDB_LANG_OPTIONS.find((o) => o.value === autoTmdbLangValue)?.label ?? autoTmdbLangValue;
  const autoRegionIso = LANG_TO_REGION[currentLang] || 'US';
  // Static ICU names also support TVs without Intl.DisplayNames.
  const regions: Record<string, string> = regionNames[currentLang] || regionNames.en;
  const autoRegionLabel = `${flagEmoji(autoRegionIso)} ${regions[autoRegionIso] || autoRegionIso}`;

  const autoLangOptionLabel = `${t('settings.language.tmdbAuto')} → ${autoTmdbLangLabel}`;
  const autoRegionOptionLabel = `${t('settings.language.regionAuto')} → ${autoRegionLabel}`;

  const appLangOptions: SelectOption[] = LANGUAGE_OPTIONS.map((o) => ({
    value: o.code,
    label: o.nativeName,
  }));

  const tmdbLangOptions: SelectOption[] = [
    { value: '', label: autoLangOptionLabel },
    ...TMDB_LANG_OPTIONS,
  ];

  const tmdbRegionOptions: SelectOption[] = [
    { value: '', label: autoRegionOptionLabel },
    ...Object.keys(regions).map(iso => ({ value: iso, label: `${flagEmoji(iso)} ${regions[iso]} (${iso})` })),
  ];

  return (
    <div className="settings-section">
      <h2 className="settings-section__title">{t('settings.language.title')}</h2>

      <div className="settings-card">
        {/* App language */}
        <div className="settings-row">
          <span className="settings-row__label">{t('settings.language.appLanguage')}</span>
          <SettingsSelect
            options={appLangOptions}
            value={currentLang}
            onChange={handleAppLangChange}
            initialFocus={focused}
          />
        </div>

        {/* TMDB content language */}
        <div className="settings-row">
          <span className="settings-row__label">{t('settings.language.tmdbLanguage')}</span>
          <SettingsSelect
            options={tmdbLangOptions}
            value={currentTmdbLang}
            onChange={handleTmdbLangChange}
          />
        </div>

        {/* TMDB region */}
        <div className="settings-row">
          <span className="settings-row__label">{t('settings.language.tmdbRegion')}</span>
          <SettingsSelect
            options={tmdbRegionOptions}
            value={currentTmdbRegion}
            onChange={handleTmdbRegionChange}
          />
        </div>
      </div>

      {/* Warning */}
      <div className="settings-warning">
        <span className="settings-warning__icon">⚠️</span>
        <div className="settings-warning__body">
          <p className="settings-warning__title">{t('settings.language.warningTitle')}</p>
          <p className="settings-warning__text">{t('settings.language.warningText')}</p>
        </div>
      </div>
    </div>
  );
};

export default LanguageSection;
